// oxlint-disable-next-line import/default -- Vite defines the worker URL module at build time.
import editorTransformWorkerUrl from './editorTransform.worker.ts?worker&url';
import type {
  EditorPreviewStylesheetResult,
  EditorTransformCacheStats,
  EditorTransformRequest,
  EditorTransformResponse,
  EditorTransformSource,
  EditorTransformSourceResult
} from './editorTransformProtocol';

/** A newer generation replaced this one before it ever reached the worker. */
export class EditorTransformSupersededError extends Error {
  constructor(generation: number) {
    super(`Editor transform generation ${generation} was superseded.`);
    this.name = 'EditorTransformSupersededError';
  }
}

/** The worker timed out, errored, or was disposed while this call was active. */
export class EditorTransformWorkerError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'EditorTransformWorkerError';
  }
}

export type EditorTransformResult = {
  generation: number;
  results: EditorTransformSourceResult[];
  stylesheet: EditorPreviewStylesheetResult | null;
  processTimeMs: number;
  cache: EditorTransformCacheStats;
};

export type EditorTransformMetric = {
  generation: number;
  sourceCount: number;
  cacheHits: number;
  durationMs: number;
  processTimeMs: number;
  cacheEntries: number;
  cacheBytes: number;
  stylesheetMs: number | null;
  stylesheetBytes: number | null;
};

declare global {
  interface Window {
    __diagonalEditorTransformMetrics?: EditorTransformMetric[];
    __diagonalPreviewWorker?: StartedEditorTransformWorker | null;
  }
}

/**
 * The part of `Worker` this client uses. Narrower than `Worker` so the tests can
 * hand it an in-thread fake without implementing the whole interface.
 */
export type EditorTransformWorkerLike = {
  postMessage(message: EditorTransformRequest): void;
  terminate(): void;
  addEventListener(type: string, listener: (event: Event) => void): void;
  /** The warm request this worker was already sent by whoever started it. */
  warmed?: EditorTransformOptions;
};

/**
 * `stylesheet` is a property of the document, not of an edit: a draft preview
 * has an empty placeholder `<style>` the worker has to fill, while a published
 * page and the component editor's admin shell get their CSS elsewhere.
 */
export type EditorTransformOptions = { stylesheet: boolean };

/**
 * A worker the draft-preview document started from an inline script in its
 * `<head>` (resources/views/preview-worker-start.blade.php), before this module
 * had downloaded. The runtime adopts it so the chunk fetch, the Tailwind core
 * import and the design-system compile it began overlap the bundle download.
 */
export type StartedEditorTransformWorker = {
  worker: EditorTransformWorkerLike;
  url: string;
  warmed: EditorTransformOptions;
};

export type EditorTransformClient = {
  transform(
    sources: EditorTransformSource[],
    options: EditorTransformOptions
  ): Promise<EditorTransformResult>;
  warm(options: EditorTransformOptions): void;
  dispose(): void;
};

export const EDITOR_TRANSFORM_TIMEOUT_MS = 10_000;

type PendingCall = {
  generation: number;
  sources: EditorTransformSource[];
  stylesheet: boolean;
  resolve: (result: EditorTransformResult) => void;
  reject: (error: Error) => void;
};

type ActiveCall = PendingCall & {
  startedAt: number;
  timeout: ReturnType<typeof setTimeout>;
};

function recordMetric(active: ActiveCall, result: EditorTransformResult): void {
  if (typeof window === 'undefined') {
    return;
  }

  (window.__diagonalEditorTransformMetrics ??= []).push({
    generation: result.generation,
    sourceCount: active.sources.length,
    cacheHits: result.results.filter((entry) => entry.ok && entry.fromCache).length,
    durationMs: performance.now() - active.startedAt,
    processTimeMs: result.processTimeMs,
    cacheEntries: result.cache.entries,
    cacheBytes: result.cache.bytes,
    stylesheetMs: result.stylesheet?.ok ? result.stylesheet.durationMs : null,
    stylesheetBytes: result.stylesheet?.ok ? result.stylesheet.css.length : null
  });
}

/**
 * Owns one transform worker and the queue in front of it.
 *
 * An author types faster than a generation completes, so the queue holds at most
 * one active call and one pending call — the latest one. Anything the latest
 * call replaces is rejected as superseded, because its transformed output could
 * only ever produce a preview the author has already moved past.
 */
export function createEditorTransformClient({
  createWorker,
  timeoutMs = EDITOR_TRANSFORM_TIMEOUT_MS
}: {
  createWorker: () => EditorTransformWorkerLike;
  timeoutMs?: number;
}): EditorTransformClient {
  let worker: EditorTransformWorkerLike | null = null;
  let generationCounter = 0;
  let active: ActiveCall | null = null;
  let pending: PendingCall | null = null;
  let disposed = false;

  function ensureWorker(): EditorTransformWorkerLike {
    if (worker) {
      return worker;
    }

    const created = createWorker();

    created.addEventListener('message', (event) => {
      if (worker === created) {
        handleResponse((event as MessageEvent<EditorTransformResponse>).data);
      }
    });

    const failWorker = () => {
      if (worker === created) {
        restart('The preview compiler stopped unexpectedly.');
      }
    };

    created.addEventListener('error', failWorker);
    created.addEventListener('messageerror', failWorker);

    worker = created;

    return created;
  }

  function post(call: PendingCall): void {
    const started: ActiveCall = {
      ...call,
      startedAt: performance.now(),
      timeout: setTimeout(() => restart('The preview compiler did not respond.'), timeoutMs)
    };

    active = started;

    try {
      ensureWorker().postMessage({
        type: 'transform',
        generation: call.generation,
        sources: call.sources,
        stylesheet: call.stylesheet
      });
    } catch {
      restart('The preview compiler could not start.');
    }
  }

  function startPending(): void {
    if (active || !pending || disposed) {
      return;
    }

    const next = pending;
    pending = null;
    post(next);
  }

  function handleResponse(response: EditorTransformResponse): void {
    if (response.type !== 'result' || !active || response.generation !== active.generation) {
      return;
    }

    const settled = active;
    clearTimeout(settled.timeout);
    active = null;

    const result: EditorTransformResult = {
      generation: response.generation,
      results: response.results,
      stylesheet: response.stylesheet,
      processTimeMs: response.processTimeMs,
      cache: response.cache
    };

    recordMetric(settled, result);
    settled.resolve(result);
    startPending();
  }

  /**
   * A worker that hung or died cannot be trusted to answer the call it holds, so
   * it is replaced outright. The caller keeps its last-good preview and decides
   * whether to ask again; a queued call is simply sent to the replacement.
   */
  function restart(message: string): void {
    worker?.terminate();
    worker = null;

    const failed = active;
    active = null;

    if (failed) {
      clearTimeout(failed.timeout);
      failed.reject(new EditorTransformWorkerError(message));
    }

    startPending();
  }

  return {
    transform(sources, { stylesheet }) {
      if (disposed) {
        return Promise.reject(new EditorTransformWorkerError('The preview compiler was disposed.'));
      }

      generationCounter += 1;
      const generation = generationCounter;

      return new Promise<EditorTransformResult>((resolve, reject) => {
        const call: PendingCall = {
          generation,
          sources,
          stylesheet,
          resolve,
          reject
        };

        if (active) {
          pending?.reject(new EditorTransformSupersededError(pending.generation));
          pending = call;

          return;
        }

        post(call);
      });
    },
    warm({ stylesheet }) {
      if (disposed) {
        return;
      }

      try {
        const target = ensureWorker();

        // A warm with a stylesheet covers one without: it loads everything the
        // smaller request would and more.
        if (target.warmed && (target.warmed.stylesheet || !stylesheet)) {
          return;
        }

        target.postMessage({ type: 'warm', stylesheet });
      } catch {
        worker?.terminate();
        worker = null;
      }
    },
    dispose() {
      disposed = true;
      worker?.terminate();
      worker = null;

      const disposedError = new EditorTransformWorkerError('The preview compiler was disposed.');

      if (active) {
        clearTimeout(active.timeout);
        active.reject(disposedError);
        active = null;
      }

      pending?.reject(disposedError);
      pending = null;
    }
  };
}

export function createEditorTransformWorker(
  workerUrl: URL,
  documentOrigin: string
): EditorTransformWorkerLike {
  if (workerUrl.origin === documentOrigin) {
    return new Worker(workerUrl, { type: 'module' });
  }

  // Worker entry points must be same-origin. The bootstrap inherits the page's
  // origin, then imports the Vite worker module through its CORS-enabled URL.
  const bootstrapUrl = URL.createObjectURL(
    new Blob([`import ${JSON.stringify(workerUrl.href)};`], {
      type: 'text/javascript'
    })
  );

  try {
    return new Worker(bootstrapUrl, { type: 'module' });
  } finally {
    URL.revokeObjectURL(bootstrapUrl);
  }
}

/**
 * Takes over the worker the document started ahead of this module, if there is
 * one and it runs the same chunk this build would start. The hand-off is
 * one-way: a second call, or a restart after a failure, gets a fresh worker.
 */
export function adoptStartedEditorTransformWorker(
  workerUrl: URL
): EditorTransformWorkerLike | null {
  const started = window.__diagonalPreviewWorker;

  if (!started) {
    return null;
  }

  window.__diagonalPreviewWorker = null;

  if (started.url !== workerUrl.href) {
    started.worker.terminate();

    return null;
  }

  return {
    warmed: started.warmed,
    postMessage: (message) => started.worker.postMessage(message),
    terminate: () => started.worker.terminate(),
    addEventListener: (type, listener) => started.worker.addEventListener(type, listener)
  };
}

function defaultCreateWorker(): EditorTransformWorkerLike {
  const workerUrl = new URL(editorTransformWorkerUrl, import.meta.url);

  return (
    adoptStartedEditorTransformWorker(workerUrl) ??
    createEditorTransformWorker(workerUrl, window.location.origin)
  );
}

let workerFactory: (() => EditorTransformWorkerLike) | null = null;
let documentClient: EditorTransformClient | null = null;
let isDocumentLifecycleBound = false;

/** Lets a test drive the real client against an in-thread worker. */
export function setEditorTransformWorkerFactory(
  factory: (() => EditorTransformWorkerLike) | null
): void {
  documentClient?.dispose();
  documentClient = null;
  workerFactory = factory;
}

function disposeDocumentClient(event: PageTransitionEvent): void {
  if (!event.persisted) {
    documentClient?.dispose();
    documentClient = null;
  }
}

/**
 * One client per authoring document — the preview iframe or the component
 * editor. Both own a document that is thrown away with its worker, so the
 * singleton's lifetime is the document's.
 */
export function getEditorTransformClient(): EditorTransformClient {
  if (!documentClient) {
    documentClient = createEditorTransformClient({
      createWorker: workerFactory ?? defaultCreateWorker
    });

    if (typeof window !== 'undefined' && !isDocumentLifecycleBound) {
      window.addEventListener('pagehide', disposeDocumentClient);
      isDocumentLifecycleBound = true;
    }
  }

  return documentClient;
}
